import React from 'react';
import { Box, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { useAvatarSession } from './useAvatarSession';
import { AvatarToggle } from './AvatarToggle';
import { PushToTalkButton } from './PushToTalkButton';

const PANEL_HEIGHT_MOBILE = 280;
const PANEL_HEIGHT_DESKTOP = 300;

/**
 * Avatar header rendered at the top of the chat panel when avatar mode is on.
 * Connection lifecycle is owned by useAvatarSession; this component only
 * renders the video element and overlays.
 */
export function AnamAvatarPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const panelHeight = isMobile ? PANEL_HEIGHT_MOBILE : PANEL_HEIGHT_DESKTOP;
  const { state, connectVideo, reattachVideo } = useAvatarSession();
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const shouldReattachOnMount = React.useRef(state === 'active');

  React.useEffect(() => {
    if (state !== 'connecting') return;
    const el = videoRef.current;
    if (!el) return;
    void connectVideo(el);
  }, [connectVideo, state]);

  // Reattach only when the panel remounts while the stream is still active
  // (e.g. chat closed then reopened). Do not run on connecting→active.
  React.useEffect(() => {
    if (!shouldReattachOnMount.current) return;
    shouldReattachOnMount.current = false;
    const el = videoRef.current;
    if (!el) return;
    void reattachVideo(el);
  }, [reattachVideo]);

  const showSpinner = state === 'connecting';

  return (
    <Box
      sx={{
        height: panelHeight,
        flexShrink: 0,
        position: 'relative',
        bgcolor: 'common.black',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            objectFit: 'cover',
            objectPosition: 'center 15%',
            display: 'block',
          }}
        />
      </Box>

      {showSpinner && (
        <Stack
          direction="column"
          alignItems="center"
          spacing={1}
          sx={{
            position: 'absolute',
            inset: 0,
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'common.white',
          }}
        >
          <CircularProgress size={28} sx={{ color: 'common.white' }} />
          <Typography variant="caption">Connecting to avatar…</Typography>
        </Stack>
      )}

      <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}>
        <AvatarToggle overlay />
      </Box>

      <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2 }}>
        <PushToTalkButton compact disabled={state !== 'active'} />
      </Box>
    </Box>
  );
}
